import { Component } from '@theme/component';
import { recommendFragrances } from '@theme/vbras-fragrance-rules';

/**
 * @typedef {Object} AdvisorRefs
 * @property {HTMLScriptElement} catalogData
 * @property {HTMLScriptElement} uiData
 * @property {HTMLButtonElement} startButton
 * @property {HTMLElement} welcome
 * @property {HTMLElement} panel
 * @property {HTMLElement} complete
 * @property {HTMLFormElement} form
 * @property {HTMLFieldSetElement[]} steps
 * @property {HTMLElement[]} stepTitles
 * @property {HTMLElement} progressText
 * @property {HTMLProgressElement} progress
 * @property {HTMLButtonElement} nextButton
 * @property {HTMLButtonElement} backButton
 * @property {HTMLElement} results
 * @property {HTMLElement} resultTitle
 * @property {HTMLElement} resultIntro
 * @property {HTMLDListElement} resultSummary
 * @property {HTMLElement} resultList
 * @property {HTMLButtonElement} partialButton
 * @property {HTMLElement} error
 * @property {HTMLElement} live
 * @property {HTMLElement} busy
 */

const ANSWER_FIELDS = ['impression', 'profile', 'family', 'occasion', 'intensity'];

/** Guided questions, using only the current Shopify collection and its real metadata. */
class VbrasFragranceAdvisor extends Component {
  /** @type {AdvisorRefs} */
  refs;

  requiredRefs = [
    'catalogData', 'uiData', 'startButton', 'welcome', 'panel', 'complete', 'form',
    'steps', 'stepTitles', 'progressText', 'progress', 'nextButton', 'backButton',
    'results', 'resultTitle', 'resultIntro', 'resultSummary', 'resultList',
    'partialButton', 'error', 'live', 'busy',
  ];

  #source;
  #ui = {};
  #products = null;
  #step = 0;
  #loading = false;
  #controller;
  #loadPromise = null;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#generation += 1;
    this.#loadPromise = null;
    this.#source = undefined;
    super.disconnectedCallback();
  }

  #initialize() {
    if (this.#source === this.refs.catalogData) return;
    this.#controller?.abort();
    this.#generation += 1;
    this.#controller = new AbortController();
    this.#source = this.refs.catalogData;
    this.#products = null;
    this.#loadPromise = null;
    this.#loading = false;
    try {
      this.#ui = JSON.parse(this.refs.uiData.textContent || '{}');
    } catch {
      this.#ui = {};
    }
    this.refs.startButton.hidden = false;
    this.#setBusy(false);
    this.#showWelcome();
  }

  /** @param {Event} event */
  async start(event) {
    event?.preventDefault();
    if (this.#loading) return;
    this.#clearError();
    this.#setBusy(true);
    this.#announce(this.#text('loading'));
    const generation = this.#generation;
    try {
      await this.#loadCatalog();
      if (!this.isConnected || generation !== this.#generation) return;
      this.#setBusy(false);
      this.#showStep(0);
      this.#announce(this.#text('readyAnnouncement'));
    } catch (error) {
      if (generation !== this.#generation || error?.name === 'AbortError') return;
      this.#setBusy(false);
      this.refs.live.textContent = '';
      this.#showError(this.#text('loadError'));
    }
  }

  /** Validate only the currently visible question. @param {SubmitEvent} event */
  advance(event) {
    event.preventDefault();
    if (this.#loading || !this.#validateStep()) return;
    this.#clearError();
    if (this.#step < this.refs.steps.length - 1) {
      this.#showStep(this.#step + 1);
      return;
    }
    this.#showResults(false);
  }

  /** @param {Event} event */
  back(event) {
    event?.preventDefault();
    if (this.#loading) return;
    if (this.#step > 0) this.#showStep(this.#step - 1);
    else {
      this.#showWelcome();
      this.refs.startButton.focus();
    }
  }

  /** Return to the questions without changing the customer's choices. @param {Event} event */
  edit(event) {
    event?.preventDefault();
    if (!this.#loading) this.#showStep(0);
  }

  /** @param {Event} event */
  restart(event) {
    event?.preventDefault();
    if (this.#loading) return;
    this.refs.form.reset();
    this.refs.resultList.replaceChildren();
    this.refs.resultSummary.replaceChildren();
    this.refs.live.textContent = '';
    this.#showWelcome();
    this.refs.startButton.focus();
  }

  /** Broaden soft preferences only after this explicit action. @param {Event} event */
  showPartial(event) {
    event?.preventDefault();
    if (!this.#loading) this.#showResults(true);
  }

  #showWelcome() {
    this.#step = 0;
    this.refs.progress.max = this.refs.steps.length;
    this.refs.progress.value = 1;
    this.refs.progressText.textContent = '';
    this.refs.welcome.hidden = false;
    this.refs.panel.hidden = true;
    this.refs.complete.hidden = true;
    this.refs.results.hidden = true;
    for (const step of this.refs.steps) {
      step.hidden = true;
      step.disabled = true;
    }
    this.#clearError();
  }

  #showStep(index) {
    this.#step = Math.max(0, Math.min(index, this.refs.steps.length - 1));
    this.refs.welcome.hidden = true;
    this.refs.panel.hidden = false;
    this.refs.complete.hidden = true;
    this.refs.results.hidden = true;
    this.refs.steps.forEach((step, stepIndex) => {
      step.hidden = stepIndex !== this.#step;
      step.disabled = stepIndex !== this.#step;
    });
    const number = this.#step + 1;
    this.refs.progress.max = this.refs.steps.length;
    this.refs.progress.value = number;
    this.refs.progressText.textContent = `${this.#text('step')} ${number} ${this.#text('of')} ${this.refs.steps.length}`;
    this.refs.nextButton.textContent = this.#text(number === this.refs.steps.length ? 'showResults' : 'next');
    this.refs.backButton.disabled = false;
    this.#clearError();
    const title = this.refs.stepTitles[this.#step];
    title?.focus({ preventScroll: true });
    title?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  #validateStep() {
    const fieldset = this.refs.steps[this.#step];
    if (!fieldset) return false;
    const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
    const budget = fieldset.querySelector('input[name="budget"]');
    if (budget && this.#budgetAnswer() === undefined) {
      this.#showError(this.#text('budgetError'));
      budget.focus();
      return false;
    }
    if (radios.length && !radios.some((radio) => radio.checked)) {
      this.#showError(this.#text('chooseOption'));
      radios[0]?.focus();
      return false;
    }
    for (const input of fieldset.querySelectorAll('input, select, textarea')) {
      if (!input.checkValidity()) {
        input.reportValidity();
        return false;
      }
    }
    return true;
  }

  #answers() {
    const answers = Object.fromEntries(ANSWER_FIELDS.map((field) => [
      field,
      this.refs.form.querySelector(`input[name="${field}"]:checked`)?.value || 'any',
    ]));
    answers.maxPrice = this.#budgetAnswer();
    return answers;
  }

  #budgetAnswer() {
    const checked = this.refs.form.querySelector('input[name="budget"]:checked');
    if (!(checked instanceof HTMLInputElement)) return undefined;
    const value = checked.value.trim();
    if (value === 'any') return null;
    if (!/^\d+$/.test(value)) return undefined;
    const cents = Number(value);
    return Number.isSafeInteger(cents) && cents >= 0 ? cents : undefined;
  }

  #showResults(allowPartial) {
    if (!this.#products) return;
    const answers = this.#answers();
    if (answers.maxPrice === undefined) {
      const budgetStep = this.refs.steps.findIndex((step) => step.querySelector('input[name="budget"]'));
      this.#showStep(Math.max(0, budgetStep));
      this.#showError(this.#text('budgetError'));
      this.refs.form.querySelector('input[name="budget"]')?.focus();
      return;
    }
    const recommendation = recommendFragrances(this.#products, answers, { limit: 3, allowPartial });
    const cards = recommendation.results.map((result) => this.#resultCard(result)).filter(Boolean);
    const noPreferences = ANSWER_FIELDS.every((field) => answers[field] === 'any');
    const hasPartial = recommendation.results.some((result) => result.unmatched.length > 0);
    this.refs.resultTitle.textContent = this.#text(
      cards.length === 0 ? 'emptyTitle' : hasPartial ? 'partialTitle' : noPreferences ? 'exploreTitle' : 'resultTitle'
    );
    this.refs.resultIntro.textContent = this.#text(
      cards.length === 0 ? 'emptyIntro' : hasPartial ? 'partialIntro' : noPreferences ? 'noPreferenceIntro' : 'exactIntro'
    );
    const completeTitle = this.refs.complete.querySelector('h2');
    const completeText = this.refs.complete.querySelector('p:not(.vbras-advisor__eyebrow)');
    if (completeTitle) completeTitle.textContent = this.#text(cards.length ? 'completeTitle' : 'emptyTitle');
    if (completeText) completeText.textContent = this.#text(cards.length ? 'completeText' : 'emptyIntro');
    this.refs.resultList.replaceChildren(...cards);
    this.#renderSummary(answers);
    this.refs.partialButton.hidden = allowPartial || recommendation.partialCount === 0 || cards.length >= 3;
    this.refs.partialButton.textContent = this.#text('partialAction');
    this.refs.welcome.hidden = true;
    this.refs.panel.hidden = true;
    this.refs.complete.hidden = false;
    this.refs.results.hidden = false;
    for (const step of this.refs.steps) step.disabled = true;
    this.#clearError();
    const announcement = this.#text('resultsAnnouncement');
    this.#announce(announcement.includes('{count}')
      ? announcement.replaceAll('{count}', String(cards.length))
      : `${announcement} ${cards.length}`);
    this.refs.resultTitle.focus({ preventScroll: true });
    this.refs.resultTitle.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  #renderSummary(answers) {
    const fragment = document.createDocumentFragment();
    for (const field of ANSWER_FIELDS) {
      const checked = this.refs.form.querySelector(`input[name="${field}"]:checked`);
      const label = this.#optionLabel(checked);
      const pair = this.#element('div', '');
      pair.append(this.#element('dt', '', this.#text(`${field}Label`)), this.#element('dd', '', label));
      fragment.append(pair);
    }
    const budget = this.#element('div', '');
    budget.append(
      this.#element('dt', '', this.#text('budgetLabel')),
      this.#element('dd', '', this.#optionLabel(this.refs.form.querySelector('input[name="budget"]:checked')))
    );
    fragment.append(budget);
    this.refs.resultSummary.replaceChildren(fragment);
  }

  #optionLabel(input) {
    const label = input?.closest('label');
    const title = label?.querySelector('[data-option-title]');
    return (title?.textContent || label?.textContent || '').trim().replace(/\s+/g, ' ');
  }

  #resultCard({ product, matches, unmatched }) {
    const url = this.#productUrl(product.url);
    if (!url) return null;
    const card = this.#element('article', 'vbras-advisor__result-card');
    const imageUrl = this.#imageUrl(product.image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'vbras-advisor__result-image';
      image.src = imageUrl;
      image.alt = typeof product.imageAlt === 'string' ? product.imageAlt : product.title || '';
      image.width = Number(product.imageWidth) > 0 ? Math.round(Number(product.imageWidth)) : 600;
      image.height = Number(product.imageHeight) > 0 ? Math.round(Number(product.imageHeight)) : 600;
      image.loading = 'lazy';
      image.decoding = 'async';
      card.append(image);
    }
    const body = this.#element('div', 'vbras-advisor__result-body');
    body.append(
      this.#element('h3', '', product.title),
      this.#element('p', 'vbras-advisor__result-price', product.priceText || this.#formatPrice(product.price))
    );
    const metadata = this.#element('dl', 'vbras-advisor__result-meta');
    const values = {
      impression: Array.isArray(product.personalities) ? product.personalities.join(' · ') : '',
      family: product.family,
      profile: product.profile,
      intensity: product.intensity,
      occasion: Array.isArray(product.occasions) ? product.occasions.join(' · ') : '',
    };
    const matchedFields = new Set(matches.map((match) => match.field));
    for (const [field, value] of Object.entries(values)) {
      if (!value || matchedFields.has(field)) continue;
      const label = this.#text(field === 'impression' ? 'personalityLabel' : `${field}Label`);
      metadata.append(this.#element('dt', '', label), this.#element('dd', '', value));
    }
    if (metadata.childElementCount) body.append(metadata);
    if (matches.length) {
      body.append(this.#element('p', '', this.#text('matchedLabel')));
      const reasons = this.#element('ul', 'vbras-advisor__result-reasons');
      for (const match of matches) {
        reasons.append(this.#element('li', '', `${this.#text(`field_${match.field}`)}: ${match.value}`));
      }
      body.append(reasons);
    }
    if (unmatched.length) {
      const labels = unmatched.map((field) => this.#text(`field_${field}`)).join(', ');
      body.append(this.#element('p', 'vbras-advisor__result-difference', `${this.#text('unmatchedLabel')}: ${labels}`));
    }
    const link = this.#element('a', 'button vbras-advisor__result-link', this.#text('viewProduct'));
    link.href = url;
    body.append(link);
    card.append(body);
    return card;
  }

  async #loadCatalog() {
    if (this.#products) return;
    if (this.#loadPromise) return this.#loadPromise;
    const promise = this.#fetchCatalog();
    this.#loadPromise = promise;
    try {
      await promise;
    } finally {
      if (this.#loadPromise === promise) this.#loadPromise = null;
    }
  }

  async #fetchCatalog() {
    const initial = this.#parseCatalog(this.refs.catalogData.textContent);
    const generation = this.#generation;
    const controller = this.#controller;
    const products = new Map(initial.products.map((product) => [String(product.id), product]));
    if (initial.page !== 1 || products.size < initial.expectedCount) {
      const root = new URL(this.dataset.catalogUrl || '/', window.location.origin);
      if (root.origin !== window.location.origin || !this.dataset.sectionId) throw new Error('Invalid catalog source');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      for (let page = 1; page <= initial.pages; page += 1) {
        if (page === initial.page) continue;
        const url = new URL(root);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('page', String(page));
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Catalog request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const component = [...html.querySelectorAll('vbras-fragrance-advisor-component')]
          .find((element) => element.dataset.sectionId === this.dataset.sectionId);
        const script = component?.querySelector('script[ref="catalogData"]');
        const catalog = this.#parseCatalog(script?.textContent);
        if (catalog.expectedCount !== initial.expectedCount || catalog.page !== page) throw new Error('Catalog changed during loading');
        for (const product of catalog.products) products.set(String(product.id), product);
      }
    }
    if (products.size < initial.expectedCount) throw new Error('Incomplete catalog');
    if (generation !== this.#generation || controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    this.#products = [...products.values()];
  }

  #parseCatalog(text) {
    const catalog = JSON.parse(text || 'null');
    if (!catalog || !Array.isArray(catalog.products)
      || !Number.isInteger(catalog.expectedCount) || catalog.expectedCount < 0
      || !Number.isInteger(catalog.page) || catalog.page < 1
      || !Number.isInteger(catalog.pages) || catalog.pages < 1
      || catalog.products.some((product) => !product || product.id === null || product.id === undefined)) {
      throw new Error('Invalid catalog');
    }
    return catalog;
  }

  #setBusy(busy) {
    this.#loading = busy;
    this.refs.startButton.disabled = busy;
    this.refs.nextButton.disabled = busy;
    this.refs.backButton.disabled = busy;
    this.refs.busy.hidden = !busy;
    this.refs.busy.textContent = busy ? this.#text('loading') : '';
    this.setAttribute('aria-busy', String(busy));
  }

  #clearError() {
    this.refs.error.textContent = '';
    this.refs.error.hidden = true;
  }

  #showError(message) {
    this.refs.error.hidden = false;
    this.refs.error.textContent = message;
  }

  #announce(message) {
    this.refs.live.textContent = message;
  }

  #text(key) {
    return typeof this.#ui[key] === 'string' ? this.#ui[key] : '';
  }

  #formatPrice(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'es', {
        style: 'currency', currency: this.dataset.currency || 'COP', minimumFractionDigits: 0, maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return String(cents / 100);
    }
  }

  #productUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin
        || !/^\/(?:[a-z]{2}(?:-[a-z0-9]{2,8})*\/)?products\/[^/]+\/?$/i.test(url.pathname)) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  #imageUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, window.location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
    } catch {
      return null;
    }
  }

  #element(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  }
}

if (!customElements.get('vbras-fragrance-advisor-component')) {
  customElements.define('vbras-fragrance-advisor-component', VbrasFragranceAdvisor);
}
